import { Injectable, OnDestroy, Injector } from '@angular/core';
import { Database, onValue, ref, off, DatabaseReference, goOffline, goOnline } from '@angular/fire/database';
import { BehaviorSubject, lastValueFrom, Observable } from 'rxjs';
import { ApiService } from '../api.service';
import { APP_CONFIG, LOCAL_STORAGE_KEY, SESSION_STORAGE_KEY } from '@core/configs';
import { LOG_CONTENT, LOG_RESULT, LOG_TYPE, NativeDiagnosticLogService } from '@core/services/diagnostic-log/native-diagnostic-log.service';
import { SessionStorageService } from '../session-storage.service';
import { FirebaseAuthService } from './firebase-auth.service';
import moment from 'moment';

import { TopicFirebase } from '@core/constants';
import { OfficeService } from '@core/services/apis/office.service';
import { TimeService } from '../time.service';
import { LocalStorageService } from '../local-storage.service';
import { FirebaseVersionService } from './firebase-version.service';
import { FirebaseDeviceSessionService } from './firebase-device-session.service';

/** Max token refreshes when the data topic returns PERMISSION_DENIED. */
const MAX_PERMISSION_RETRY = 10;

/** Max token refreshes when the .info/connected listener reports an auth error. */
const MAX_AUTH_REFRESH_RETRY = 10;

/** Reason for calling subscribeToDataTopic — decides how newSyncDate is computed on the first snapshot. */
type SubscribeReason = 'initialLoad' | 'reconnect' | 'permissionDeniedRetry';

@Injectable({
  providedIn: 'root'
})
export class FirebaseDatabaseService implements OnDestroy {

  // ── Firebase references ──
  private dataTopicRef: DatabaseReference | null = null;       // office data node (d/{corp}/{office}/s)
  private connectionStateRef: DatabaseReference | null = null; // .info/connected

  // ── Data change stream (public) ──
  private readonly dataChangeSubject = new BehaviorSubject<any>(null);
  public readonly onFirebaseDataChange$: Observable<any> = this.dataChangeSubject.asObservable();

  // ── Disconnect state (public) ──
  private readonly disconnectedSubject = new BehaviorSubject<boolean>(false);
  /** Emits true when the network drops (after the first connect), false on reconnect or logout. */
  public readonly isFirebaseDisconnected$ = this.disconnectedSubject.asObservable();

  // ── Internal state ──
  private isDataTopicSubscribed = false;
  private corpPrefix = '';
  private officeSelected: any;
  public hasConnectedBefore = false;
  private removeConnectionStateListener?: () => void;
  private authRefreshRetryCount = 0;
  private isConnecting = false;
  private isRealtimeConnected = false;
  private isHealthCheckInProgress = false;

  constructor(
    private readonly http: ApiService,
    private db: Database,
    private sessionStorage: SessionStorageService,
    private firebaseAuth: FirebaseAuthService,
    private injector: Injector,
    private timeService: TimeService,
    private localStorage: LocalStorageService,
    private deviceSession: FirebaseDeviceSessionService,
    private versionService: FirebaseVersionService,
    private nativeDiagnosticLogService: NativeDiagnosticLogService
  ) {
    this.onAppBootstrap();
  }

  // ═══════════════════════ FLOW: APP BOOTSTRAP (F5 / startup) ════════════════════
  private async onAppBootstrap() {
    console.log('[Firebase] onAppBootstrap called');
    this.versionService.watchAppVersionNode(TopicFirebase.WEB_APP_VERSION);
    this.versionService.watchAppVersionNode(TopicFirebase.TV_APP_VERSION);
    this.connectRealtime();
  }

  // ═══════════════════════ FLOW: INIT (after office selection / login) ════════════
  public initializeFirebase(): Promise<boolean> {
    console.log('[Firebase] initializeFirebase called');
    // sessionsFBId is created/reused inside deviceSession (requestDeviceSessionToken),
    // so the device keeps the same token across F5 / re-init. connectRealtime calls goOnline internally.
    return this.connectRealtime();
  }

  // ═══════════════════════ FLOW: LOGOUT ════════════════════════════════════════════════
  public async logoutFirebaseRealtimeDB(deviceToken?: string) {
    if (!APP_CONFIG.useFirebase) return;

    await this.deviceSession.removeSession(deviceToken);

    this.disconnectRealtime();
    this.connectionStateRef = null;
    this.isRealtimeConnected = false;
    this.disconnectedSubject.next(false);
  }

  /**
   * Heartbeat health check: proactively refresh an expiring ID token through the
   * Firebase SDK (falling back to a backend custom token), then restore missing
   * listeners/device presence or retry a disconnected Realtime Database.
   */
  public async checkFirebaseHealth(): Promise<boolean> {
    if (!APP_CONFIG.useFirebase) return true;
    if (this.isHealthCheckInProgress || !this.tryLoadOfficeContext()) return false;

    this.isHealthCheckInProgress = true;
    try {

      // Step 1: Kiểm tra xem token Firebase có sắp hết hạn không, nếu có thì refresh token
      const tokenExpiringSoon = await this.firebaseAuth.isIdTokenExpiringSoon();
      if (tokenExpiringSoon) {
        // Normal periodic renewal stays inside Firebase Auth and does not call our backend.
        // A custom-token refresh is only the fallback when Firebase cannot refresh itself.
        let refreshed = await this.firebaseAuth.refreshIdToken();
        if (!refreshed) {
          refreshed = await this.firebaseAuth.fireAuthRefreshToken();
        }
        if (!refreshed) {
          console.warn('[Firebase] Health check: unable to refresh the Firebase ID token.');
          return false;
        }
      }

      // Step 2: Kiểm tra xem các listener quan trọng có còn tồn tại không, nếu không thì reconnect
      const hasConnectionListener = !!this.connectionStateRef && !!this.removeConnectionStateListener;
      const hasDataTopicListener = !!this.dataTopicRef && this.isDataTopicSubscribed;
      const hasForceTopicListeners = this.versionService.hasForceTopicListeners;
      const hasDeviceSession = this.deviceSession.isRegistered;

      const hasRequiredListeners = hasConnectionListener
        && hasDataTopicListener
        && hasDeviceSession
        && hasForceTopicListeners;

      // Missing refs/session require a full registration. A transient offline state
      // does not: Firebase SDK retains the listeners and can reconnect by goOnline().
      if (!hasRequiredListeners) {
        console.warn('[Firebase] Health check: missing listener or device session, re-registering...');
        const reconnected = await this.connectRealtime(true);
        if (!reconnected) {
          console.warn('[Firebase] Health check: reconnect attempt was not completed.');
          return false;
        }
        return true;
      }

      if (!this.isRealtimeConnected) {
        console.warn('[Firebase] Health check: Realtime Database is offline, requesting SDK reconnect...');
        goOnline(this.db);
        return false;
      }

      return true;
    } catch (error) {
      console.warn('[Firebase] Health check failed:', error);
      return false;
    } finally {
      this.isHealthCheckInProgress = false;
    }
  }

  // ═══════════════════ CONNECTION STATE MACHINE (.info/connected) ═══════════════════
  // Covers the first-connect FLOW and the reconnect FLOW (network lost → restored).
  private async connectRealtime(resetConnectionState = true): Promise<boolean> {
    if (this.isConnecting) {
      console.log('[Firebase] connectRealtime: skipped (a connect is already in progress)');
      return false;
    }
    this.isConnecting = true;
    if (resetConnectionState) {
      // A new office/login must not retain listeners from the previous lifecycle.
      this.unsubscribeFromDataTopic();
      this.versionService.unwatchForceTopics();
      this.unwatchConnectionState();
    }

    try {
      if (!this.tryLoadOfficeContext()) {
        // Debug localhost without Firebase: emit a single data change so the UI updates.
        console.log('[Firebase] connectRealtime: debug branch (no office context / Firebase disabled) → NO .info/connected listener attached');
        this.emitOfflineDataChange();
        return true;
      }

      // Ensure the SDK is online — required after logout called goOffline().
      goOnline(this.db);

      // Detach the old listener before re-initializing to avoid duplicate sessions.
      this.unwatchConnectionState();
      if (resetConnectionState) {
        this.hasConnectedBefore = false;
      }

      this.connectionStateRef = ref(this.db, '.info/connected');
      if (!this.connectionStateRef) return false;

      await this.deviceSession.register(this.corpPrefix, this.officeSelected.officeCd);
      if (!this.deviceSession.isRegistered) return false;

      this.removeConnectionStateListener = onValue(
        this.connectionStateRef,
        (snapshot) => this.onConnectionStateChanged(snapshot.val() === true),
        (error) => this.onConnectionAuthError(error)
      );
      console.log('[Firebase] connectRealtime: .info/connected listener attached');
      return true;
    } catch (error) {
      console.error('[Firebase] connectRealtime failed before attaching .info/connected listener:', error);
      return false;
    } finally {
      this.isConnecting = false;
    }
  }

  private async onConnectionStateChanged(isConnected: boolean) {
    console.log('[Firebase] Connected state:', isConnected);
    this.isRealtimeConnected = isConnected;

    if (!isConnected) {
      console.log('[Firebase] Disconnected, waiting for reconnect...');
      if (this.hasConnectedBefore) {
        this.disconnectedSubject.next(true); // network lost → show popup
      }
      return;
    }

    this.disconnectedSubject.next(false); // reconnected → hide popup
    this.authRefreshRetryCount = 0;

    const dataTopic = `d/${this.corpPrefix}/${this.officeSelected.officeCd}/s`;
    console.log('[Firebase] hasConnectedBefore: ', this.hasConnectedBefore);

    if (this.hasConnectedBefore) {
      await this.handleReconnect(dataTopic);
    } else {
      this.handleFirstConnect(dataTopic);
    }
  }

  // ── FLOW: FIRST CONNECT ──
  private handleFirstConnect(dataTopic: string) {
    this.hasConnectedBefore = true;

    // presence + onDisconnect already set in deviceSession.register().
    // Subscribe AFTER connect + auth confirmed to avoid PERMISSION_DENIED.
    this.sessionStorage.setSessionData(SESSION_STORAGE_KEY.topicName, dataTopic);
    void this.subscribeToDataTopic(dataTopic, 0, 'initialLoad');
    this.versionService.watchForceTopics(this.corpPrefix, this.officeSelected.officeCd);
  }

  // ── FLOW: RECONNECT ──
  private async handleReconnect(dataTopic: string) {
    try {
      console.log('[Firebase] handleReconnect called');
      await this.deviceSession.renew(this.corpPrefix, this.officeSelected.officeCd);
    } catch (err) {
      // Temporary backend failure → log and stop. The Firebase SDK will reconnect next time.
      console.error('[Firebase] renewDeviceSession failed, will retry on next reconnect:', err);
      return;
    }
    // subscribeToDataTopic('reconnect') forces hasChange=true on the first snapshot so the UI reloads.
    // (Previously there was an extra manual next() here → caused a duplicate emit, now removed.)
    this.sessionStorage.setSessionData(SESSION_STORAGE_KEY.topicName, dataTopic);
    void this.subscribeToDataTopic(dataTopic, 0, 'reconnect');
  }

  // Auth token expired → Firebase stops retrying, so refresh the token and re-initialize.
  private async onConnectionAuthError(error: any) {
    if (this.authRefreshRetryCount >= MAX_AUTH_REFRESH_RETRY) {
      console.log(`[Firebase] Max auth refresh retries reached, stopping to prevent infinite loop, error: ${error?.message}`);
      this.authRefreshRetryCount = 0;
      return;
    }
    this.authRefreshRetryCount++;
    const refreshed = await this.firebaseAuth.fireAuthRefreshToken();
    if (refreshed) {
      void this.connectRealtime(false);
    }
  }

  // ═══════════════════════ DATA TOPIC SUBSCRIPTION ═════════════════════════════════════
  // reason='initialLoad'          : F5 / login — use lastSyncDate from sessionStorage (already fresh from API).
  // reason='reconnect'            : network restored — refresh lastSyncDate from API then use sessionStorage.
  // reason='permissionDeniedRetry': after PERMISSION_DENIED — refresh lastSyncDate from API then use sessionStorage.
  // reason=undefined              : subsequent data change from backend — always use moment().
  private async subscribeToDataTopic(topic: string, retryCount = 0, reason?: SubscribeReason) {
    if (this.isDataTopicSubscribed) {
      this.unsubscribeFromDataTopic();
    }

    // Refresh lastSyncDate from the API before subscribing (reconnect / permissionDeniedRetry).
    if (reason === 'reconnect' || reason === 'permissionDeniedRetry') {
      console.log(`[Firebase] reason = ${reason}, retryCount = ${retryCount}`);
      await this.refreshLatestSyncDate();
    }
    this.dataTopicRef = ref(this.db, topic);
    this.isDataTopicSubscribed = true;
    let isFirstSnapshot = true;

    onValue(
      this.dataTopicRef,
      (snapshot) => {
        const val = snapshot.val();
        let hasChange = typeof val === 'string' && val.startsWith('+');
        // Raw topic value, surfaced for scope-aware consumers. Bath changes write a
        // sentinel ("2-{timestamp}") with no '+' prefix, so hasChange stays false and only
        // bathing-schedule (which checks topicVal) reacts — check-sheet/excreation don't.
        const topicVal = val;
        // The first snapshot replays whatever /s already held, which may be minutes or hours
        // old. Consumers deriving "changed" from topicVal must ignore it — a retained value is
        // not an event. (hasChange already handles this via the isFirstSnapshot branch below.)
        const isFirst = isFirstSnapshot;
        let newSyncDate: string;

        if (isFirstSnapshot && reason) {
          // First snapshot: use lastSyncDate from sessionStorage (already fresh).
          newSyncDate = this.sessionStorage.getSessionData(SESSION_STORAGE_KEY.lastSyncDate) || moment().format('YYYY-MM-DD HH:mm:ss');
          // Reconnect: force a reload since data may have changed while offline.
          if (reason === 'reconnect') {
            hasChange = true;
          }
        } else {
          // Real data change from backend: update to the current time.
          newSyncDate = moment().format('YYYY-MM-DD HH:mm:ss');
          this.sessionStorage.setSessionData(SESSION_STORAGE_KEY.lastSyncDate, newSyncDate);
        }
        isFirstSnapshot = false;

        this.nativeDiagnosticLogService.sendCareMoniLog({
          logType: LOG_TYPE.FIREBASE,
          subject: topic,
          status: hasChange ? LOG_CONTENT.HAS_CHANGE : LOG_CONTENT.NO_CHANGE,
          message: LOG_CONTENT.AUTO_SYNC_DETECTED 
        });

        this.timeService.setTimeSyncFireBase(newSyncDate);
        this.dataChangeSubject.next({ hasChange, newSyncDate, topicVal, isFirstSnapshot: isFirst });
      },
      (error) => this.retryDataTopicOnPermissionDenied(error, topic, retryCount)
    );
  }

  // PERMISSION_DENIED on the data topic → refresh the token then re-subscribe.
  private async retryDataTopicOnPermissionDenied(error: any, topic: string, retryCount: number) {
    if (error.code !== 'PERMISSION_DENIED' || retryCount >= MAX_PERMISSION_RETRY) return;

    const refreshed = await this.firebaseAuth.fireAuthRefreshToken();
    if (refreshed) {
      // refreshLatestSyncDate is handled inside subscribeToDataTopic.
      void this.subscribeToDataTopic(topic, retryCount + 1, 'permissionDeniedRetry');
    }
  }

  private unsubscribeFromDataTopic() {
    if (this.dataTopicRef && this.isDataTopicSubscribed) {
      off(this.dataTopicRef);
      this.isDataTopicSubscribed = false;
    }
  }

  // ═══════════════════════ HELPERS / TEARDOWN ══════════════════════════════════════════

  /**
   * Force a data reload (e.g. when the heartbeat detects the device is out of sync
   * with the backend — DeviceRegistrations heartbeat returned 'needsDataSync').
   */
  public triggerDataSync(): void {
    const newSyncDate = moment().format('YYYY-MM-DD HH:mm:ss');
    this.sessionStorage.setSessionData(SESSION_STORAGE_KEY.lastSyncDate, newSyncDate);
    this.timeService.setTimeSyncFireBase(newSyncDate);
    this.dataChangeSubject.next({ hasChange: true, newSyncDate });
  }

  private async refreshLatestSyncDate(): Promise<void> {
    try {
      const corpPrefix = this.localStorage.getItem(LOCAL_STORAGE_KEY.corpPrefix);
      this.officeSelected = this.sessionStorage.getSessionData(SESSION_STORAGE_KEY.officeSelected);
      if (!corpPrefix || !this.officeSelected || !this.officeSelected?.officeCd) return;
      const officeService = this.injector.get(OfficeService);
      const res = await lastValueFrom(officeService.getLatestSyncDate(this.officeSelected.officeCd, corpPrefix));
      const latestSyncDate = res?.result?.latestSyncDate;
      if (latestSyncDate) {
        this.sessionStorage.setSessionData(SESSION_STORAGE_KEY.lastSyncDate, latestSyncDate);
      }
    } catch (error) {
      console.warn('Failed to refresh latestSyncDate from API:', error);
    }
  }

  /** Load corpPrefix + officeSelected from storage and check whether Firebase is usable. */
  private tryLoadOfficeContext(): boolean {
    this.corpPrefix = this.localStorage.getItem(LOCAL_STORAGE_KEY.corpPrefix) || '';
    this.officeSelected = this.sessionStorage.getSessionData(SESSION_STORAGE_KEY.officeSelected);
    return !!(this.corpPrefix && this.officeSelected && APP_CONFIG.useFirebase);
  }

  /** Debug localhost without Firebase: emit one data change so the UI renders. */
  private emitOfflineDataChange() {
    this.corpPrefix = this.localStorage.getItem(LOCAL_STORAGE_KEY.corpPrefix) || '';
    this.officeSelected = this.sessionStorage.getSessionData(SESSION_STORAGE_KEY.officeSelected);
    if (this.corpPrefix && this.officeSelected) {
      const newSyncDate = this.sessionStorage.getSessionData(SESSION_STORAGE_KEY.lastSyncDate) || moment().format('YYYY-MM-DD HH:mm:ss');
      this.timeService.setTimeSyncFireBase(newSyncDate);
      this.dataChangeSubject.next({ hasChange: true, newSyncDate });
    }
  }

  private unwatchConnectionState() {
    if (this.removeConnectionStateListener) {
      this.removeConnectionStateListener();
      this.removeConnectionStateListener = undefined;
    }
  }

  /** Fully disconnect from Firebase Realtime Database. */
  private disconnectRealtime() {
    if (!APP_CONFIG.useFirebase) return;
    this.unsubscribeFromDataTopic();
    this.versionService.unwatchForceTopics();
    this.unwatchConnectionState();
    this.isRealtimeConnected = false;
    // App version listeners (web/tv) are app-level — do NOT detach here since they're still needed after re-login;
    // only detached in ngOnDestroy via versionService.destroy().
    goOffline(this.db);
  }

  ngOnDestroy(): void {
    this.disconnectRealtime();
    this.versionService.destroy();
    this.disconnectedSubject.complete();
  }
}
